<template>
  <div>
    <div id="chatbot-button">
      <button id="open-interface" @click="toggleChatbot"></button>
    </div>
    <div
      id="chatbot-container"
      v-show="isChatbotOpen || isClosing"
      :class="{
        'chatbot-opening': isChatbotOpen,
        'chatbot-closing': isClosing,
      }">
      <div id="chatbot-header">
        <div class="chatbot-header-container">
          <span id="chatbot-header-title">PremaAssist</span>
        </div>
        <div class="chatbot-header-container">
          <img :src="botAvatar" alt="bot" />
          <button
            class="chatbot-control-btn"
            @click="closeChatbot"
            title="Close">
            ×
          </button>
        </div>
      </div>
      <div id="chat-messages" ref="chatMessages">
        <div
          v-for="(message, index) in messages"
          :key="index"
          :class="['message', message.role + '-message']"
          v-html="message.content"></div>
        <div v-if="showSuggestions" class="message suggestions-message">
          <div
            v-for="(suggestion, index) in suggestions"
            :key="index"
            class="suggestion"
            @click="handleSuggestionClick(suggestion)">
            {{ suggestion }}
          </div>
        </div>
        <div v-if="isWaiting" class="message ai-message loading-message">
          <img :src="loadingGif" alt="Loading..." />
        </div>
      </div>
      <div id="user-input">
        <input
          type="text"
          id="message-input"
          maxlength="2048"
          placeholder="Type your message here..."
          v-model="userMessage"
          @keydown.enter="sendMessage" />
        <button id="send-button" @click="sendMessage">
          <img :src="sendIcon" alt="send" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { io } from 'socket.io-client';

export default {
  data() {
    return {
      isChatbotOpen: false,
      isClosing: false,
      botAvatar: require('./images/bot_avatar_man.png'),
      sendIcon: require('./images/send.png'),
      loadingGif: require('./images/loading.gif'),
      userMessage: '',
      messages: [],
      isWaiting: false,
      socket: null,
      showSuggestions: true,
      suggestions: [
        '❓ What is PREMA?',
        '💻 How do I create an account?',
        '🔒 What are the key sections of the PREMA standard?',
      ],
      inactivityTimer: null,
      chatbotClosed: false,
    };
  },
  methods: {
    closeChatbot() {
      this.disconnectSocket();
      this.messages = [];
      this.showSuggestions = true;
      this.isChatbotOpen = false;
      this.isClosing = false;
      this.chatbotClosed = true;
    },
    toggleChatbot() {
      if (this.isChatbotOpen) {
        this.isClosing = true;
        setTimeout(() => {
          this.isChatbotOpen = false;
          this.isClosing = false;
        }, 500);
      } else {
        if (this.chatbotClosed) {
          this.setupSocket();
        }
        this.isChatbotOpen = true;
      }
    },
    disconnectSocket() {
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
      }
    },
    resetInactivityTimer() {
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
      }
      this.inactivityTimer = setTimeout(() => {
        this.restartChatbot();
      }, 10 * 60 * 1000); // 10 minutes
    },
    restartChatbot() {
      if (this.socket) {
        this.socket.disconnect();
        this.socket.connect();
      }
      this.messages = [];
      this.showSuggestions = true;
    },
    sendMessage() {
      if (this.userMessage.trim() === '') return;

      this.resetInactivityTimer();

      // Add the user's message
      this.messages.push({ content: this.userMessage, role: 'user' });

      // Clear the input field
      const messageToSend = this.userMessage;
      this.userMessage = '';

      // Hide suggestions after the first message
      this.showSuggestions = false;

      // Show the waiting state
      this.isWaiting = true;

      // Emit the message to the backend
      this.socket.emit('user_message', { message: messageToSend });

      // Scroll to the bottom
      this.scrollToBottom();
    },
    handleSuggestionClick(suggestion) {
      this.userMessage = suggestion;
      this.sendMessage();
    },
    setupSocket() {
      const token = this.$store.state.auth.accessToken;

      this.socket = io('https://chatbot.tisalabs.com', {
        transports: ['websocket'],
        auth: {
          token: token,
        },
      });

      this.socket.on('bot_message', (response) => {
        this.isWaiting = false;

        const aiResponse =
          response.text || "I'm sorry, I couldn't process your request.";
        this.messages.push({ content: `🤖 ${aiResponse}`, role: 'ai' });

        // Scroll to the bottom
        this.scrollToBottom();
      });

      this.socket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
      });
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatMessages;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
  },
  mounted() {
    this.setupSocket();
  },
  unmounted() {
    this.disconnectSocket();
  },
  beforeDestroy() {
    if (this.socket) {
      this.socket.disconnect();
    }
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@200..800&display=swap');

* {
  font-family: 'Manrope', sans-serif;
}

.chatbot-control-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  margin-left: 8px;
  color: #ffffff;
}

.chatbot-control-btn:hover {
  color: #000;
}

#open-interface {
  position: fixed;
  bottom: 70px;
  right: 15px;
  width: 50px;
  height: 50px;
  background-image: url('images/chat.png');
  background-repeat: no-repeat;
  background-position: center;
  background-color: #7f2628;
  color: #ffffff;
  border-radius: 50%;
  text-align: center;
  line-height: 50px;
  cursor: pointer;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.2);
  border: #909090 solid 1px;
  z-index: 99999999;
}

#chatbot-container {
  z-index: 99999999;
  position: fixed;
  bottom: 115px;
  right: 70px;
  width: 350px;
  height: 500px;
  background-color: #f5f5f5;
  border: none;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 4px 10px;
  transform-origin: bottom right;
}

#chatbot-header {
  background-color: #7f2628;
  color: #ffffff;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

#chatbot-header-title {
  font-weight: 600;
  color: #ffffff;
}

#chatbot-header img {
  height: 40px;
  width: 40px;
  border-radius: 50%;
  margin-top: 5px;
}

#chat-messages {
  height: 80%;
  overflow-y: auto;
  padding: 10px;
  font-size: small;
}

#chat-messages::-webkit-scrollbar {
  width: 8px;
}

#chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
}

#chat-messages::-webkit-scrollbar-thumb {
  background: #a0a0a0;
  border-radius: 4px;
}

#chat-messages::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.suggestions-message {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px;
  border-radius: 10px;
  margin-top: 10px;
}

.suggestion {
  background-color: #ffffff;
  border: 1px solid #d3d3d3;
  border-radius: 20px;
  padding: 8px 15px;
  font-size: 14px;
  color: #7f2628;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
  margin-left: -10px;
  margin-top: 2px;
}

.suggestion:hover {
  background-color: #7f2628;
  color: #ffffff;
}

#user-input {
  background-color: #7f2628;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

#message-input {
  border: none;
  border-radius: 5px;
  padding: 10px;
  width: 80%;
  height: 35px;
  color: #000000;
  background-color: #ffffff;
}

#send-button {
  background-color: #7f2628;
  color: #ffffff;
  border: 2px solid #7f2628;
  border-radius: 5px;
  padding: 5px 10px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.3s ease, transform 0.1s ease;
}

#send-button:hover {
  background-color: #5e1d1f;
}

#send-button:active {
  background-color: #4a1618;
  transform: scale(0.95);
}

#send-button img {
  filter: invert(1);
}

@keyframes chatbotOpenAnimation {
  0% {
    transform: scale(0.1) translate(200%, 200%);
    opacity: 0;
  }
  100% {
    transform: scale(1) translate(0, 0);
    opacity: 1;
  }
}

@keyframes chatbotCloseAnimation {
  0% {
    transform: scale(1) translate(0, 0);
    opacity: 1;
  }
  100% {
    transform: scale(0.1) translate(200%, 200%);
    opacity: 0;
  }
}

.chatbot-opening {
  animation: chatbotOpenAnimation 0.5s ease-out;
}

.chatbot-closing {
  animation: chatbotCloseAnimation 0.5s ease-out;
}

.message {
  padding: 10px;
  margin: 5px 0;
  border-radius: 10px;
  max-width: 80%;
  display: block;
  clear: both;
  vertical-align: top;
  word-wrap: break-word;
}

.user-message {
  background-color: #7f2628;
  color: #ffffff;
  border-radius: 10px;
  float: right;
}

.ai-message {
  background-color: #ffffff;
  color: #000000;
  border: 1px solid #d3d3d3;
  margin-right: auto;
  border-radius: 10px;
  float: left;
}

.loading-message img {
  height: 30px;
}
</style>
